import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { EditButton } from '~/src/components/atoms/Button/EditButton/EditButton'
import { INotificationsResponse, TNotificationItem } from '~/src/repositories/Notification'
import { TablePagination, TablePaginationActions } from '~/src/components/molecules'
import { formatDate } from '~/src/libs'

type Params = {
  limit: number
  offset?: number
}

interface ITableNotificationsProps {
  notifications: INotificationsResponse['data']['notifications']
  pagination: INotificationsResponse['data']['pagination']
  onPageChange: (_params?: Params) => void
}

/**
 * TableNotifications
 * @param __namedParameters ITableNotificationsProps
 * @returns JSX.Element
 * @category ReactComponents
 */
export const TableNotifications: React.FC<ITableNotificationsProps> = ({
  notifications,
  pagination,
  onPageChange,
}: ITableNotificationsProps) => {
  const { t } = useTranslation()
  const rows = notifications?.map((item: TNotificationItem) => item)
  const navigate = useNavigate()
  const location = useLocation()

  const [page, setPage] = useState(pagination ? pagination.currentPage - 1 : 0)

  /**
   * handleChangePage
   * @param event
   * @param newPage
   */
  const handleChangePage = (event: unknown, newPage: number): void => {
    console.log({ event, newPage })
    setPage(newPage)
    onPageChange({ offset: newPage + 1, limit: 20 })
  }

  return rows ? (
    <Paper sx={{ width: '100%', pt: 1, px: 1, pb: 2 }}>
      <TableContainer>
        <Table
          sx={{ minWidth: 500 }}
          aria-labelledby="tableTitle"
          size={'medium'}
          stickyHeader
          aria-label="sticky table"
        >
          <TableHead>
            <TableRow>
              <TableCell
                align={'left'}
                padding={'normal'}
                sx={{ width: '118px', paddingRight: 0, background: 'white' }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '118px',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    component="p"
                    variant="body2"
                    fontSize={14}
                    fontWeight={500}
                    color={'#000000DE'}
                  >
                    {t('notification.date')}
                  </Typography>
                  <Typography
                    sx={{
                      height: '14px',
                      width: '2px',
                      backgroundColor: '#E0E0E0',
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell
                align={'left'}
                padding={'normal'}
                sx={{ paddingRight: 0, background: 'white' }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    component="p"
                    variant="body2"
                    fontSize={14}
                    fontWeight={500}
                    color={'#000000DE'}
                  >
                    {t('notification.message')}
                  </Typography>
                  <Typography
                    sx={{
                      height: '14px',
                      width: '2px',
                      backgroundColor: '#E0E0E0',
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell
                align={'center'}
                padding={'normal'}
                sx={{ width: '118px', background: 'white' }}
              >
                <Typography
                  component="p"
                  variant="body2"
                  fontSize={14}
                  fontWeight={500}
                  color={'#000000DE'}
                >
                  {t('notification.edit_label')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: TNotificationItem) => (
              <TableRow hover tabIndex={-1} key={row.id}>
                <TableCell
                  align={'left'}
                  padding={'normal'}
                  sx={{ width: '118px', paddingRight: 0, background: 'white' }}
                >
                  <Typography component="p" variant="body2" fontSize={14} color={'#222222'}>
                    {formatDate(row.openDate, 'yyyy/MM/dd')}
                  </Typography>
                </TableCell>
                <TableCell align={'left'} padding={'normal'} sx={{ background: 'white' }}>
                  <Typography component="p" variant="body2" fontSize={14} color={'#000000DE'}>
                    {row.message}
                  </Typography>
                </TableCell>
                <TableCell
                  align={'center'}
                  padding={'normal'}
                  sx={{ width: '118px', background: 'white' }}
                >
                  <EditButton
                    text={'notification.edit'}
                    handleEdit={() => {
                      navigate(`/admin/news/edit?id=${row.id}`, {
                        state: {
                          item: row,
                          prevLocationState: { ...location }
                        }
                      })
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        count={pagination ? pagination.totalCount : 0}
        rowsPerPage={pagination ? pagination.limitValue : 20}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationActions}
      />
    </Paper>
  ) : null
}
